import { useTerminologyStore } from '@/shared/stores/terminology.store';
import type { TerminologyMap } from '@/features/settings/domain/entities/TerminologyDefaults';
import type { UserRole } from '@/features/auth/domain/value-objects/UserRole';

/**
 * Convenience hook for accessing terminology values.
 *
 * Returns the full terminology map plus helpers:
 * - `getRoleLabel(role)` — maps a UserRole to the configured display name
 * - `getForumRoleLabel(forumRole)` — maps a ForumUserRole string to the configured display name
 */
export function useTerminology() {
  const terminology = useTerminologyStore((s) => s.terminology);

  const getRoleLabel = (role: UserRole): string => {
    const roleMap: Record<UserRole, keyof TerminologyMap> = {
      director: 'roleDirector',
      teacher: 'roleTeacher',
      student: 'roleStudent',
      admin: 'roleDirector',
    };

    if (role === 'admin') return 'Administrador';
    return terminology[roleMap[role]];
  };

  /**
   * Maps a ForumUserRole string ('Director' | 'Profesor' | 'Alumno')
   * to the configured terminology label.
   */
  const getForumRoleLabel = (forumRole: string): string => {
    const forumRoleMap: Record<string, keyof TerminologyMap> = {
      Director: 'roleDirector',
      Profesor: 'roleTeacher',
      Alumno: 'roleStudent',
    };

    const key = forumRoleMap[forumRole];
    return key ? terminology[key] : forumRole;
  };

  return {
    ...terminology,
    getRoleLabel,
    getForumRoleLabel,
  };
}
